import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import alloyService from '../services/alloy.service';
import { setMessage } from './message';


export const fetchAlloysList = createAsyncThunk(
  'alloys/fetchAlloyList',
  async ({ page, pagesize, search, ordering }, thunkAPI) => {
    try {
      const response = await alloyService.getAlloyList(page, pagesize, search, ordering);
      return response.data; // Return the full response
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const fetchAlloyById = createAsyncThunk(
  'alloys/fetchAlloyById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await alloyService.getAlloyById(id);
      return response.data; // Return the profile data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAlloyDropdown = createAsyncThunk(
  'alloys/fetchAlloyDropdown',
  async ({ page, pagesize, search, ordering }, thunkAPI) => {
    try {
      const response = await alloyService.getAlloyDropdown(page, pagesize, search, ordering);
      return response.data; // Return the full response
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue();
    }
  },
);
export const fetchArchivedAlloysList = createAsyncThunk(
  'alloyslist/fetchArchiveAlloyList',
  async ({ page, pagesize, search, ordering }, thunkAPI) => {
    try {
      const response = await alloyService.getArchiveAlloyList(page, pagesize, search, ordering);
      return response.data; // Return the full response
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue();
    }
  },
);
export const createAlloy = createAsyncThunk(
  'alloyslist/createAlloysList',
  async (sizeData, thunkAPI) => {
    try {
      const response = await alloyService.addAlloy(sizeData);
      return response.data || response.results; // Assuming response contains the created alloy data
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const editAlloy = createAsyncThunk(
  'alloy/editAlloy',
  async ({ id, values }, thunkAPI) => {
    try {
      const response = await alloyService.updateAlloy(id, values);
      return response.data; // Ensure this returns the updated category data
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);
export const archiveAlloy = createAsyncThunk(
  'alloy/archiveAlloy',
  async (id, thunkAPI) => {
    try {
      await alloyService.archiveAlloy(id);
      return id; // Return the ID of the deleted alloy
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  },
);
export const unArchiveAlloy = createAsyncThunk(
  'alloy/unArchiveAlloy',
  async (id, thunkAPI) => {
    try {
      await alloyService.unArchiveAlloy(id);
      return id; // Return the ID of the unarchived alloy
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue();
    }
  },
);

const initialState = {
  data: [], // Data for current page
  list: [], // All fetched data (combined from multiple pages)
  loading: false,
  totalRecord: 0,
  totalPages: 0,
  currentPage: 1,
  status: 'idle',
  error: null,
  hasMore: true,
};

const alloySlice = createSlice({
  name: 'alloy',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    resetAlloyList: state => {
      state.list = []; // Clear the entire list
      state.data = []; // Also clear the current page data
    },
    resetCurrentAlloyListPage: state => {
      state.currentPage = 1;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAlloysList.pending, state => {
        state.status = 'loading';
        state.loading = false;
      })
      .addCase(fetchAlloysList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const fetchedPage = action.payload.page || state.currentPage;
        state.data = action.payload.results.data || []; // Correctly access the data array
        state.totalRecord = action.payload.count || 0; // Set total records
        state.totalPages = Math.ceil((action.payload.count || 0) / action.meta.arg.limit); // Calculate total pages
        state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
        state.list = [...state.list, ...action.payload.results.data];
        state.hasMore = !!action.payload.next;
      })
      .addCase(fetchAlloyDropdown.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
    builder
      .addCase(fetchAlloyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlloyById.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(fetchAlloyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(fetchAlloyDropdown.pending, state => {
        state.status = 'loading';
        state.loading = false;
      })
      .addCase(fetchAlloyDropdown.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.payload) {
          const fetchedPage = action.payload.page || state.currentPage;
          const resultsData = action.payload.results?.data || action.payload.results || action.payload.data || [];
          state.data = Array.isArray(resultsData) ? resultsData : []; // Correctly access the data array
          state.totalRecord = action.payload.count || 0; // Set total records
          state.totalPages = Math.ceil((action.payload.count || 0) / (action.meta?.arg?.pagesize || action.meta?.arg?.limit || 10)); // Calculate total pages
          state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
          state.list = [...(Array.isArray(state.list) ? state.list : []), ...(Array.isArray(resultsData) ? resultsData : [])];
          state.hasMore = !!action.payload.next;
        }
      })

    builder
      .addCase(fetchArchivedAlloysList.pending, state => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(fetchArchivedAlloysList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const fetchedPage = action.payload.page || state.currentpage;
        state.data = action.payload.results.data || [];  // Update to match your API response
        state.totalRecord = action.payload.count || 0; // Handle count (total records)
        state.totalPages = action.payload.totalPages || 0; // Handle totalPages
        state.currentpage = action.payload.next ? fetchedPage + 1 : fetchedPage;
        state.list = [...state.list, ...action.payload.results.data];
        state.hasMore = !!action.payload.next;
      })
      .addCase(fetchArchivedAlloysList.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(createAlloy.pending, state => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(createAlloy.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        state.list.push(action.payload); // Add the newly created alloy to the list
      })
      .addCase(createAlloy.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(editAlloy.pending, state => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(editAlloy.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.data.findIndex(Size => Size.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload; // Update the alloy in the state
        }
      })
      .addCase(editAlloy.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(archiveAlloy.pending, state => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(archiveAlloy.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
        state.list = state.list.filter(group => group.id !== action.payload); // Remove the deleted group from list
      })
      .addCase(archiveAlloy.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(unArchiveAlloy.pending, state => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(unArchiveAlloy.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = state.data.filter(group => group.id !== action.payload); // Remove the unarchived group from state
        state.list = state.list.filter(group => group.id !== action.payload); // Remove the unarchived group from list
      })
      .addCase(unArchiveAlloy.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { resetAlloyList, resetCurrentAlloyListPage } = alloySlice.actions;
const { reducer } = alloySlice;
export default reducer;
